/**
 * StatusBadge — el estado de un documento, una cobranza o una liquidación.
 *
 * Portado de `clobi-design-system@0.1.0`. Lo que el sistema aporta y el repo no
 * tenía es el **catálogo cerrado**: el estado deja de pintarse a mano en cada
 * pantalla —47 pastillas con colores crudos en 97 archivos— y pasa a salir de
 * una tabla única. Si falta un estado, es un hueco del catálogo y se agrega acá,
 * no un color que se inventa en la celda.
 *
 * ─── 🔴 Los catálogos son los del REPO, no los del sistema ────────────────────
 *
 * Los del sistema se escribieron sin ver el modelo, y tres claves no coinciden:
 *
 *     sistema            repo (SaleDocument.STATUS_CHOICES)
 *     sent           →   sent_facturador
 *     querying       →   querying_status
 *     accepted_repairs   no existe
 *
 * Portarlos tal cual habría hecho que un documento «Enviado al facturador»
 * mostrara la cadena `sent_facturador` en la tabla: el componente cae a la clave
 * cuando no encuentra la entrada. Es el mismo fallo que ya tuvo el desglose de la
 * reforma de pensiones, donde `crp_rate` se mostró crudo por una etiqueta que
 * faltaba.
 *
 * Cada clave de acá está verificada contra el `STATUS_CHOICES` de su modelo, y
 * hay un test que falla si el modelo gana un estado que el catálogo no tiene.
 *
 * ⚠️ **Los RÓTULOS también son los del producto, no los del sistema.** El
 * sistema propone «Enviado al facturador», «Consultando estado» y «Aceptado
 * SII»; las pantallas muestran «Enviado», «Consultando» y «Aceptado» desde
 * siempre, y coinciden entre sí. Cambiar lo que el usuario ya lee sería un
 * cambio de producto disfrazado de refactor.
 *
 * Lo que SÍ cambia con el port es el color: diez paletas sueltas —con cuatro
 * en tonos crudos que no siguen el tema— pasan a cinco tonos semánticos, que
 * es justamente lo que el catálogo viene a unificar.
 */
import type * as React from 'react'
import type { CSSProperties, ReactNode } from 'react'

import {
  DTE_STATUS,
  type StatusCatalog,
  type StatusTone,
} from './statusCatalogs'

export {
  DTE_STATUS,
  COLLECTION_STATUS,
  PAYMENT_STATUS,
  PAYROLL_PERIOD_STATUS,
  PAYSLIP_STATUS,
  SETTLEMENT_STATUS,
  VACATION_REQUEST_STATUS,
  LEAVE_REQUEST_STATUS,
  PAYABLE_STATUS,
  VOUCHER_STATUS,
  SIGNATURE_STATUS,
} from './statusCatalogs'
export type { StatusCatalog, StatusEntry, StatusTone } from './statusCatalogs'

/**
 * El color de cada tono.
 *
 * ⚠️ `neutral` envuelve sus tokens en `rgb()` y los demás no, y no es un
 * descuido: las familias de estado del repo son colores completos, mientras
 * `--rgb-glass` y `--color-content-muted` son canales sueltos. Sin el `rgb()` el
 * navegador descarta la propiedad **en silencio** y la pastilla sale
 * transparente.
 */
const TONOS: Record<StatusTone, { bg: string; fg: string }> = {
  neutral: {
    bg: 'rgb(var(--rgb-glass) / var(--alpha-glass-3))',
    fg: 'rgb(var(--color-content-muted))',
  },
  info: { bg: 'var(--color-info-surface)', fg: 'var(--color-info-content)' },
  success: { bg: 'var(--color-success-surface)', fg: 'var(--color-success-content)' },
  warning: { bg: 'var(--color-warning-surface)', fg: 'var(--color-warning-content)' },
  danger: { bg: 'var(--color-danger-surface)', fg: 'var(--color-danger-content)' },
}

/**
 * Además de sus props, acepta los atributos de un `<span>`: `title` —que varias
 * celdas usan para explicar un estado sin alargar la tabla— y los `aria-*`.
 */
export type StatusBadgePropsConAtributos = StatusBadgeProps &
  Omit<React.HTMLAttributes<HTMLSpanElement>, 'style' | 'className'>

export interface StatusBadgeProps {
  /** La clave dentro del catálogo. */
  status?: string
  /** `DTE_STATUS` por defecto. */
  catalog?: StatusCatalog
  /** Pisa el texto del catálogo. Solo para un estado de negocio que no esté. */
  label?: ReactNode
  tone?: StatusTone
  live?: boolean
  dot?: boolean
  className?: string
  style?: CSSProperties
}

export function StatusBadge({
  status,
  catalog = DTE_STATUS,
  label,
  tone,
  live,
  dot = true,
  className = '',
  style,
  ...rest
}: StatusBadgePropsConAtributos) {
  const entrada = (status && catalog[status]) || undefined
  const t = TONOS[tone ?? entrada?.tone ?? 'neutral']
  const late = live ?? entrada?.live ?? false
  // Sin entrada ni `label` queda la clave cruda. Es feo a propósito: se ve en la
  // pantalla y se corrige agregándola al catálogo, en vez de pasar inadvertido.
  const texto = label ?? entrada?.label ?? status ?? ''

  return (
    <span
      className={`cl-badge ${late ? 'cl-badge--live ' : ''}${className}`.trim()}
      style={{ background: t.bg, color: t.fg, ...style }}
      {...rest}
    >
      {dot && <span className="cl-badge__dot" aria-hidden="true" />}
      {texto}
    </span>
  )
}
